import React, {
    FC,
    Fragment,
    ReactNode,
    useEffect,
    useRef,
    useState
} from "react";
import { createPortal } from "react-dom";

type DropdownContentProps = {
    children: ReactNode;
    triggerRef: React.RefObject<Element | null>;
    open: boolean;
    onClose?: () => void;
    className?: string;
    fullWidth?: boolean;
};

const AppDropdownContent: FC<DropdownContentProps> = ({
    children,
    triggerRef,
    open = false,
    onClose,
    className,
    fullWidth = true
}) => {
    const [ready, setReady] = useState(false);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const updatePosition = () => {
        if (!triggerRef.current || !dropdownRef.current) return;

        const rect = triggerRef.current.getBoundingClientRect();

        const dropdownHeight = dropdownRef.current.offsetHeight;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const shouldDropUp = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;
        const top = shouldDropUp ? rect.top - 4 : rect.bottom + 4;
        dropdownRef.current.style.left = rect.left + "px";
        dropdownRef.current.style.top = top + "px";
        if (fullWidth) {
            dropdownRef.current.style.minWidth = rect.width + "px";
        }
        dropdownRef.current.style.transform = shouldDropUp
            ? "translateY(-100%)"
            : "none";
    };

    useEffect(() => {
        if (!open) return;
        setReady(false);

        let frameId: number;

        const loop = () => {
            updatePosition();
            frameId = requestAnimationFrame(loop);
        };

        frameId = requestAnimationFrame(loop);
        const t = setTimeout(() => setReady(true), 0);
        return () => {
            cancelAnimationFrame(frameId);
            clearTimeout(t);
        };
    }, [open]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                !triggerRef.current?.contains(e.target as Node) &&
                !dropdownRef.current?.contains(e.target as Node)
            ) {
                onClose?.();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <Fragment>
            {open &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        className={`fixed z-[2147483648] transition-opacity duration-100 ${className || ""}`}
                        style={{
                            minWidth: 0,
                            opacity: ready ? 1 : 0,
                            pointerEvents: ready ? "auto" : "none"
                        }}
                    >
                        {children}
                    </div>,
                    document.body
                )}
        </Fragment>
    );
};

export default AppDropdownContent;